import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    Alert,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    Tooltip,
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    WhatsApp,
    CheckCircle,
    Cancel,
} from '@mui/icons-material';
import { whatsappAPI, companyAPI } from '../../services/api';
import { selectIsSuperAdmin, selectUser } from '../../store/authSlice';

const WhatsAppNumbers = () => {
    const isSuperAdmin = useSelector(selectIsSuperAdmin);
    const user = useSelector(selectUser);
    const [numbers, setNumbers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [editingNumber, setEditingNumber] = useState(null);
    const [formData, setFormData] = useState({
        company_id: '',
        phone_number_id: '',
        phone_number: '',
        display_name: '',
        department: '',
        access_token: '',
        webhook_verify_token: '',
        waba_id: '',
        is_active: true
    });

    useEffect(() => {
        fetchNumbers();
        if (isSuperAdmin) {
            fetchCompanies();
        }
    }, []);

    const fetchNumbers = async () => {
        try {
            setLoading(true);
            const response = await whatsappAPI.getNumbers();
            setNumbers(response.data.data);
            setError('');
        } catch (err) {
            setError('Failed to load WhatsApp numbers');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await companyAPI.getAll();
            setCompanies(response.data.data);
        } catch (err) {
            console.error('Failed to load companies:', err);
        }
    };

    const handleOpenDialog = (number = null) => {
        if (number) {
            setEditingNumber(number);
            setFormData({
                company_id: number.company_id || '',
                phone_number_id: number.phone_number_id,
                phone_number: number.phone_number || '',
                display_name: number.display_name,
                department: number.department || '',
                access_token: '', // Don't show existing token
                webhook_verify_token: number.webhook_verify_token || '',
                waba_id: number.waba_id || '',
                is_active: number.is_active
            });
        } else {
            setEditingNumber(null);
            setFormData({
                company_id: isSuperAdmin ? '' : user.company_id,
                phone_number_id: '',
                phone_number: '',
                display_name: '',
                department: '',
                access_token: '',
                webhook_verify_token: '',
                waba_id: '',
                is_active: true
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingNumber(null);
        setFormData({
            company_id: '',
            phone_number_id: '',
            phone_number: '',
            display_name: '',
            department: '',
            access_token: '',
            webhook_verify_token: '',
            waba_id: '',
            is_active: true
        });
    };

    const handleSubmit = async () => {
        try {
            const submitData = { ...formData };

            // Don't send empty access_token on update
            if (editingNumber && !submitData.access_token) {
                delete submitData.access_token;
            }

            if (editingNumber) {
                await whatsappAPI.updateNumber(editingNumber.id, submitData);
            } else {
                await whatsappAPI.createNumber(submitData);
            }
            handleCloseDialog();
            fetchNumbers();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save WhatsApp number');
        }
    };

    const handleDelete = async (numberId) => {
        if (window.confirm('Are you sure you want to delete this WhatsApp number? All conversations will still be preserved.')) {
            try {
                await whatsappAPI.deleteNumber(numberId);
                fetchNumbers();
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to delete WhatsApp number');
            }
        }
    };

    const handleToggleStatus = async (number) => {
        try {
            await whatsappAPI.updateNumber(number.id, { is_active: !number.is_active });
            fetchNumbers();
        } catch (err) {
            setError('Failed to update status');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight={600}>
                        WhatsApp Numbers
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Manage WhatsApp Business API numbers and departments
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                >
                    Add Number
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Display Name</TableCell>
                            {isSuperAdmin && <TableCell>Company</TableCell>}
                            <TableCell>Phone Number</TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell>Phone Number ID</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {numbers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={isSuperAdmin ? 8 : 7} align="center">
                                    <Box sx={{ py: 3 }}>
                                        <WhatsApp sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                        <Typography color="text.secondary">
                                            No WhatsApp numbers configured yet
                                        </Typography>
                                        <Button 
                                            variant="outlined" 
                                            startIcon={<Add />}
                                            onClick={() => handleOpenDialog()}
                                            sx={{ mt: 2 }}
                                        >
                                            Add Your First Number
                                        </Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            numbers.map((number) => (
                                <TableRow key={number.id} hover>
                                    <TableCell>
                                        <Tooltip title={number.is_active ? 'Active' : 'Inactive'}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleToggleStatus(number)}
                                                color={number.is_active ? 'success' : 'default'}
                                            >
                                                {number.is_active ? <CheckCircle /> : <Cancel />}
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <WhatsApp color="success" />
                                            <Typography fontWeight={500}>{number.display_name}</Typography>
                                        </Box>
                                    </TableCell>
                                    {isSuperAdmin && (
                                        <TableCell>{number.company_name || '-'}</TableCell>
                                    )}
                                    <TableCell>
                                        <Typography variant="body2" fontFamily="monospace">
                                            {number.phone_number || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {number.department ? (
                                            <Chip 
                                                label={number.department} 
                                                size="small" 
                                                color="primary" 
                                                variant="outlined"
                                            />
                                        ) : (
                                            <Typography color="text.secondary" variant="body2">General</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                                            {number.phone_number_id.substring(0, 20)}...
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {new Date(number.created_at).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleOpenDialog(number)}
                                        >
                                            <Edit />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDelete(number.id)}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingNumber ? 'Edit WhatsApp Number' : 'Add New WhatsApp Number'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
                        {isSuperAdmin && (
                            <FormControl fullWidth required>
                                <InputLabel>Company</InputLabel>
                                <Select
                                    value={formData.company_id}
                                    label="Company"
                                    onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                                    disabled={!!editingNumber}
                                >
                                    {companies.map((company) => (
                                        <MenuItem key={company.id} value={company.id}>
                                            {company.company_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <TextField
                            fullWidth
                            label="Phone Number ID"
                            value={formData.phone_number_id}
                            onChange={(e) => setFormData({ ...formData, phone_number_id: e.target.value })}
                            required
                            disabled={!!editingNumber}
                            helperText="Meta WhatsApp Phone Number ID from Business Manager"
                        />

                        <TextField
                            fullWidth
                            label="Display Phone Number"
                            value={formData.phone_number}
                            onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                            placeholder="+91 98765 43210"
                            helperText="For display purposes (optional)"
                        />

                        <TextField
                            fullWidth
                            label="Display Name"
                            value={formData.display_name}
                            onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                            required
                            placeholder="e.g., Sales Department, Customer Support"
                        />

                        <FormControl fullWidth>
                            <InputLabel>Department</InputLabel>
                            <Select
                                value={formData.department}
                                label="Department"
                                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                            >
                                <MenuItem value="">
                                    <em>General (No Department)</em>
                                </MenuItem>
                                <MenuItem value="sales">Sales</MenuItem>
                                <MenuItem value="support">Support</MenuItem>
                                <MenuItem value="technical">Technical</MenuItem>
                                <MenuItem value="billing">Billing</MenuItem>
                                <MenuItem value="regional">Regional Office</MenuItem>
                                <MenuItem value="custom">Custom Department</MenuItem>
                            </Select>
                        </FormControl>

                        {formData.department === 'custom' && (
                            <TextField
                                fullWidth
                                label="Custom Department Name"
                                value={formData.department}
                                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                                placeholder="Enter custom department name"
                            />
                        )}

                        <TextField
                            fullWidth
                            label="Access Token"
                            type="password"
                            value={formData.access_token}
                            onChange={(e) => setFormData({ ...formData, access_token: e.target.value })}
                            required={!editingNumber}
                            helperText={editingNumber ? "Leave empty to keep current token" : "Meta WhatsApp API Access Token"}
                        />

                        <TextField
                            fullWidth
                            label="Webhook Verify Token"
                            value={formData.webhook_verify_token}
                            onChange={(e) => setFormData({ ...formData, webhook_verify_token: e.target.value })}
                            helperText="Optional: Custom webhook verification token"
                        />

                        <TextField
                            fullWidth
                            label="WhatsApp Business Account ID (WABA)"
                            value={formData.waba_id}
                            onChange={(e) => setFormData({ ...formData, waba_id: e.target.value })}
                            helperText="Optional: For reference"
                        />

                        {editingNumber && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    />
                                }
                                label="Active"
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {editingNumber ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WhatsAppNumbers;
